/**
 * Breadcrumbs system for tracking user actions before errors occur.
 * Helps with debugging by providing context about what the user was doing.
 */

export type BreadcrumbType = 'navigation' | 'action' | 'mutation' | 'error';

export interface Breadcrumb {
  timestamp: string;
  type: BreadcrumbType;
  message: string;
  metadata?: Record<string, unknown>;
}

const MAX_BREADCRUMBS = 20;

class BreadcrumbsManager {
  private breadcrumbs: Breadcrumb[] = [];

  /**
   * Add a breadcrumb to the trail
   */
  add(type: BreadcrumbType, message: string, metadata?: Record<string, unknown>): void {
    const breadcrumb: Breadcrumb = {
      timestamp: new Date().toISOString(),
      type,
      message,
      metadata,
    };

    this.breadcrumbs.push(breadcrumb);

    // Keep only the most recent breadcrumbs
    if (this.breadcrumbs.length > MAX_BREADCRUMBS) {
      this.breadcrumbs = this.breadcrumbs.slice(-MAX_BREADCRUMBS);
    }
  }

  /**
   * Track navigation to a new route
   */
  navigation(path: string, metadata?: Record<string, unknown>): void {
    this.add('navigation', `→ ${path}`, metadata);
  }

  /**
   * Track a user action (button click, form open, etc.)
   */
  action(action: string, metadata?: Record<string, unknown>): void {
    this.add('action', action, metadata);
  }

  /**
   * Track a mutation start or completion
   */
  mutation(
    name: string,
    status: 'start' | 'success' | 'error',
    metadata?: Record<string, unknown>
  ): void {
    const statusEmoji = status === 'start' ? '⏳' : status === 'success' ? '✓' : '✗';
    this.add('mutation', `${statusEmoji} ${name}`, metadata);
  }

  /**
   * Track an error
   */
  error(message: string, metadata?: Record<string, unknown>): void {
    this.add('error', message, metadata);
  }

  /**
   * Get all breadcrumbs
   */
  getAll(): Breadcrumb[] {
    return [...this.breadcrumbs];
  }

  /**
   * Get the most recent N breadcrumbs
   */
  getRecent(count: number = 5): Breadcrumb[] {
    return this.breadcrumbs.slice(-count);
  }

  /**
   * Clear all breadcrumbs
   */
  clear(): void {
    this.breadcrumbs = [];
  }

  /**
   * Format breadcrumbs for display in error reports
   */
  formatForReport(count: number = 10): string {
    const recent = this.getRecent(count);
    if (recent.length === 0) {
      return 'Nessuna azione recente';
    }

    return recent
      .map((b) => {
        const time = new Date(b.timestamp).toLocaleTimeString('it-IT', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
        return `- ${time} [${b.type}] ${b.message}`;
      })
      .join('\n');
  }
}

// Singleton instance
export const breadcrumbs = new BreadcrumbsManager();
